import React, { useState } from "react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { toast } from "sonner";
import { Arrow120 } from "@/components/Arrow120";

const Field = ({ id, label, ...props }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.25em] text-ahm-teal/70 font-semibold mb-2">{label}</span>
    <input
      id={id}
      name={id}
      data-testid={`contact-${id}`}
      {...props}
      className="w-full bg-transparent border-b border-ahm-teal/25 focus:border-ahm-orange py-3 text-lg text-ahm-teal outline-none transition-colors placeholder:text-ahm-teal/40"
    />
  </label>
);

const Contact = () => {
  const { config } = useSite();
  const [form, setForm] = useState({ name: "", phone: "", email: "", who: "", purpose: "", heardFrom: "", message: "" });
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Name, phone and email are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact_submissions", form);
      toast.success("Thanks! We'll reach out within 24 hours.");
      setForm({ name: "", phone: "", email: "", who: "", purpose: "", heardFrom: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-28 md:pt-40 pb-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs uppercase tracking-[0.3em] text-ahm-orange font-semibold mb-6">Say hello</p>
          <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85] text-ahm-teal text-balance">
            Let's <em className="not-italic text-ahm-orange">talk</em>.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-ahm-teal/80 leading-relaxed">
            Tell us a little about your brand. We'll get back within one working day with thoughts, a quote, or just a question or two.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <form onSubmit={submit} className="md:col-span-7 space-y-8" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field id="name" label="Your name" value={form.name} onChange={update("name")} placeholder="e.g., Anika Reddy" />
              <Field id="phone" label="Phone" value={form.phone} onChange={update("phone")} placeholder="+91 ..." />
              <Field id="email" label="Email" type="email" value={form.email} onChange={update("email")} placeholder="you@brand.com" />
              <Field id="who" label="Who's reaching out?" value={form.who} onChange={update("who")} placeholder="Founder / Marketing lead / Agency" />
              <Field id="purpose" label="What for?" value={form.purpose} onChange={update("purpose")} placeholder="Social, film, ads, identity..." />
              <Field id="heardFrom" label="Where did you hear about us?" value={form.heardFrom} onChange={update("heardFrom")} placeholder="Instagram, referral..." />
            </div>
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.25em] text-ahm-teal/70 font-semibold mb-2">Tell us more</span>
              <textarea
                rows={5}
                id="message"
                name="message"
                data-testid="contact-message"
                value={form.message}
                onChange={update("message")}
                placeholder="A paragraph about your brand and what you'd like help with."
                className="w-full bg-transparent border-b border-ahm-teal/25 focus:border-ahm-orange py-3 text-lg text-ahm-teal outline-none placeholder:text-ahm-teal/40 resize-none"
              />
            </label>
            <button type="submit" disabled={busy} className="btn-pill group" data-testid="contact-submit-button">
              {busy ? "Sending…" : "Send message"}
              <Arrow120 />
            </button>
          </form>

          <aside className="md:col-span-5 space-y-8">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-ahm-orange font-semibold mb-3">Studio</p>
              <p className="font-display text-2xl text-ahm-teal leading-snug">{config.address}</p>
            </div>
            <div className="grid grid-cols-2 gap-8 text-sm text-ahm-teal/85">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-ahm-orange font-semibold mb-2">Email</p>
                <a className="hover:text-ahm-orange" href={`mailto:${config.email}`}>{config.email}</a>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-ahm-orange font-semibold mb-2">Phone</p>
                <a className="hover:text-ahm-orange" href={`tel:${(config.phone || "").replace(/\s/g, "")}`}>{config.phone}</a>
              </div>
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-ahm-teal/15">
              <iframe
                src={config.map_embed}
                title="map"
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Contact;
